// Greenhold <OriginLinkFooter> — the discovery-flywheel link (Story 4.4).
//
// Renders the origin-trail link below every Certificate + share-detail
// hero. Walks the user from a share back to the dossier of origin
// (live or museum). Drives the UX-DR20 + UX-spec L1584-L1611
// discovery flywheel.
//
// Story 4.4 — the FIRST production OriginLinkFooter component (Story 4.3
// stage-set the placeholder span; Story 4.4 swaps in the real
// micro-animated version). Two variants per UX-spec L1593-L1595:
//   * `inline` (default) — monospace 10px gold, used on every
//     <Certificate> render-site whenever a polish task opts each
//     consumer in.
//   * `prominent` — monospace 12px gold, used on the share-detail
//     hero footer.
//
// Hover micro-animation: thin gold underline scales-X from left to
// right (0.4s) via Tailwind transform utilities + group-hover. CSS-
// driven (not framer-motion) for hover so pointer-events on the
// underline overlay don't fight the link's own hover surface.
//
// Click/unmount micro-animation: framer-motion AnimatePresence exit
// animation — the link visibly "follows" the cursor (x: 10) + fades
// during route navigation (UX-spec L1600). framer-motion is already
// in the @greenhold/components bundle (Stories 3.3 Certificate + 3.6
// RevealCeremony are existing consumers — no new dependency).

import type { FC } from "react";
import { motion } from "framer-motion";

import { cn } from "../lib/utils";
import { formatIssueNumber, type CertificateShare } from "./Certificate";

export type OriginLinkFooterVariant = "inline" | "prominent";

export interface OriginLinkFooterProps {
  share: CertificateShare;
  /** `inline` (default) for embedded use on Certificate render-sites;
   *  `prominent` for the share-detail hero footer. */
  variant?: OriginLinkFooterVariant;
  /** Optional className for wrapping consumers (e.g., centering layout). */
  className?: string;
}

const MAX_TITLE_CHARS = 40;

/** Truncate strings longer than 40 chars per UX-spec L1608. Inline
 *  helper — single consumer; anti-premature-abstraction. */
function truncate(s: string): string {
  if (s.length <= MAX_TITLE_CHARS) return s;
  return s.slice(0, MAX_TITLE_CHARS - 1) + "…";
}

export const OriginLinkFooter: FC<OriginLinkFooterProps> = ({
  share,
  variant = "inline",
  className,
}) => {
  const sizeClass = variant === "prominent" ? "text-xs" : "text-[10px]";
  const ariaLabel = `Trace back to source: ${share.portfolio_title}, dossier ${share.edition_number}`;
  return (
    <motion.span
      className={cn("inline-block", className)}
      initial={{ opacity: 1, x: 0 }}
      exit={{ x: 10, opacity: 0 }}
      transition={{ duration: 0.3 }}
    >
      <a
        href={`/shop/${share.drop_id}`}
        aria-label={ariaLabel}
        className={cn(
          "group relative inline-flex items-baseline font-mono text-tier-founders-fill",
          "focus:outline-none focus-visible:ring-2 focus-visible:ring-tier-founders-fill focus-visible:ring-offset-2",
          sizeClass,
        )}
      >
        <span>
          ↳ Issued from {truncate(share.portfolio_title)} · Dossier #
          {formatIssueNumber(share.edition_number)}
        </span>
        {/* Dotted resting underline — visible at all times for
            discoverability per UX-spec L1591. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 -bottom-0.5 border-b border-dotted border-tier-founders-fill/60"
        />
        {/* Animated solid underline — scales from origin-left on
            hover/focus per UX-spec L1599 (golden traceback line
            micro-animation). transition-transform handles the 0.4s
            timing the spec calls out. */}
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute inset-x-0 -bottom-0.5 h-px",
            "origin-left scale-x-0 bg-tier-founders-fill",
            "transition-transform duration-[400ms] ease-out",
            "group-hover:scale-x-100 group-focus-visible:scale-x-100",
          )}
        />
      </a>
    </motion.span>
  );
};
